import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Upload, CheckCircle, AlertCircle, FileText, Camera, File, Loader2, Shield, Lock, X, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API_URL = typeof window !== "undefined" ? window.location.origin : "";

const PROFILE_LABELS = {
  bailleur: "Bailleur",
  proprietaire_occupant: "Propriétaire Occupant",
  sci: "SCI"
};

const getFileIcon = (filename) => {
  if (!filename) return <File className="w-5 h-5 text-gray-500" />;
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <Camera className="w-5 h-5 text-blue-500" />;
  if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

export default function ClientUploadPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [showComplementary, setShowComplementary] = useState(false);
  const [complementaryFiles, setComplementaryFiles] = useState([]);
  const [uploadingComplementary, setUploadingComplementary] = useState(false);
  const fileInputRefs = useRef({});
  const cameraInputRefs = useRef({});
  const complementaryRef = useRef(null);
  const complementaryCameraRef = useRef(null);

  useEffect(() => {
    const fetchLinkInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/upload/${token}`);
        if (response.status === 404) {
          setError("invalid");
        } else if (response.status === 410) {
          setError("expired");
        } else if (response.ok) {
          const data = await response.json();
          setClientInfo(data);
          const uploaded = {};
          (data.files_uploaded || []).forEach(f => {
            if (f.document_key) uploaded[f.document_key] = f;
          });
          setUploadedDocs(uploaded);
          const compFiles = (data.files_uploaded || []).filter(f => !f.document_key || f.document_key === "complementaire");
          setComplementaryFiles(compFiles);
        } else {
          setError("unknown");
        }
      } catch (err) {
        setError("network");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchLinkInfo();
  }, [token]);

  const handleUploadForDoc = async (docKey, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    if (!allowed.includes(ext)) {
      toast.error("Format non accepté. Utilisez PDF, JPG, PNG, DOC ou DOCX");
      return;
    }
    setUploadingKey(docKey);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", docKey);
    formData.append("document_key", docKey);
    try {
      const response = await fetch(`${API_URL}/api/portal/upload/${token}`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setUploadedDocs(prev => ({
          ...prev,
          [docKey]: { file_id: data.file_id, filename: file.name, uploaded_at: new Date().toISOString() }
        }));
        toast.success(`${file.name} envoyé !`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setUploadingKey(null);
    }
  };

  const handleComplementaryUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    setUploadingComplementary(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("document_type", "complementaire");
    formData.append("document_key", "");
    try {
      const response = await fetch(`${API_URL}/api/portal/upload/${token}`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        setComplementaryFiles(prev => [...prev, { filename: file.name, uploaded_at: new Date().toISOString() }]);
        toast.success(`${file.name} envoyé !`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setUploadingComplementary(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
          <p className="text-slate-600 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-card rounded-2xl shadow-xl p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-1">RENOVA Solution</h1>
          
          {error === "expired" ? (
            <>
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto my-5">
                <AlertCircle className="w-7 h-7 text-amber-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-700 mb-2">Lien expiré</h2>
              <p className="text-slate-500 text-sm">
                Ce lien de dépôt a expiré. Veuillez contacter votre conseiller RENOVA pour obtenir un nouveau lien.
              </p>
            </>
          ) : error === "invalid" ? (
            <>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto my-5">
                <X className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-700 mb-2">Lien invalide</h2>
              <p className="text-slate-500 text-sm">
                Ce lien n'est pas valide. Vérifiez que vous avez copié l'URL complète.
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto my-5">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-slate-700 mb-2">Erreur de connexion</h2>
              <p className="text-slate-500 text-sm">
                Impossible de charger la page. Vérifiez votre connexion internet et réessayez.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const requiredDocs = clientInfo?.required_documents || [];
  const profileLabel = clientInfo?.profile_label || PROFILE_LABELS[clientInfo?.profile] || "";
  const uploadedCount = Object.keys(uploadedDocs).filter(k => requiredDocs.some(d => d.key === k)).length;
  const allRequiredDone = requiredDocs.length > 0 && requiredDocs.every(d => uploadedDocs[d.key]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header - compact on mobile */}
      <header className="bg-gradient-to-r from-[#1a3a2f] to-[#2d5a47] py-3 sm:py-4 px-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-500 rounded-lg flex items-center justify-center shadow-md shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-white tracking-wide leading-tight">RENOVA</h1>
            <p className="text-emerald-400 text-[10px] sm:text-xs font-medium tracking-widest">SOLUTION</p>
          </div>
          <div className="ml-auto">
            <div className="flex items-center gap-1.5 text-emerald-300 text-xs">
              <Lock className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sécurisé</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-8">
        {/* Welcome card */}
        <div className="bg-card rounded-xl sm:rounded-2xl shadow-md p-4 sm:p-5 mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-1 leading-tight">
            Bonjour {clientInfo?.client_name}
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
            Déposez vos documents justificatifs ci-dessous. Cliquez sur chaque document pour l'ajouter depuis vos fichiers ou prendre une photo.
          </p>
          {profileLabel && (
            <div className="mt-2.5 inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-700 px-2.5 py-1 rounded-lg text-xs sm:text-sm font-medium">
              <FileText className="w-3.5 h-3.5" />
              Profil : {profileLabel}
            </div>
          )}
        </div>

        {/* Security badge - compact */}
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 mb-4 flex items-center gap-2.5">
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
            <Lock className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800 text-xs sm:text-sm">Transfert 100% sécurisé</p>
            <p className="text-emerald-700 text-[10px] sm:text-xs">Vos documents sont chiffrés et transmis de manière sécurisée.</p>
          </div>
        </div>

        {/* Required documents */}
        {requiredDocs.length > 0 && (
          <div className="bg-card rounded-xl sm:rounded-2xl shadow-md p-4 sm:p-5 mb-4">
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-0.5 flex items-center gap-2">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 shrink-0" />
              Documents requis
            </h3>
            <p className="text-[10px] sm:text-xs text-slate-500 mb-3">
              Cliquez pour déposer le fichier ou prendre une photo.
            </p>

            {/* Progress bar at top */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-500">{uploadedCount} / {requiredDocs.length} déposés</span>
                {allRequiredDone && (
                  <span className="text-emerald-600 font-medium flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Complet !
                  </span>
                )}
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 sm:h-2">
                <div
                  className="bg-emerald-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                  style={{ width: `${requiredDocs.length > 0 ? (uploadedCount / requiredDocs.length) * 100 : 0}%` }}
                />
              </div>
            </div>

            <div className="space-y-2 sm:space-y-2.5">
              {requiredDocs.map((doc) => {
                const isUploaded = !!uploadedDocs[doc.key];
                const isUploading = uploadingKey === doc.key;
                const wasAlreadySubmitted = doc.status === "awaiting_validation" || doc.status === "validated";

                return (
                  <div
                    key={doc.key}
                    className={`relative border rounded-xl transition-all ${
                      isUploaded || wasAlreadySubmitted
                        ? 'border-emerald-500/40 bg-emerald-500/10/60'
                        : 'border-slate-200 active:border-emerald-400 active:bg-slate-50'
                    }`}
                    data-testid={`doc-upload-${doc.key}`}
                  >
                    {/* Main content area */}
                    <div className="p-3 sm:p-3.5">
                      <div className="flex items-start gap-2.5 sm:gap-3">
                        {/* Status icon */}
                        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                          isUploaded || wasAlreadySubmitted ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          {isUploading ? (
                            <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                          ) : isUploaded || wasAlreadySubmitted ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <Upload className="w-4 h-4 text-slate-400" />
                          )}
                        </div>

                        {/* Doc info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-xs sm:text-sm leading-tight">{doc.name}</p>
                          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 leading-snug">{doc.description}</p>
                          {isUploaded && (
                            <p className="text-[10px] sm:text-xs text-emerald-600 mt-1 flex items-center gap-1 truncate">
                              <CheckCircle className="w-3 h-3 shrink-0" />
                              <span className="truncate">{uploadedDocs[doc.key]?.filename || "Document envoyé"}</span>
                            </p>
                          )}
                          {wasAlreadySubmitted && !isUploaded && (
                            <p className="text-[10px] sm:text-xs text-emerald-600 mt-1">Déjà déposé</p>
                          )}
                        </div>
                      </div>

                      {/* Action buttons row - full width on mobile */}
                      {!isUploaded && !wasAlreadySubmitted && !isUploading && (
                        <div className="flex gap-2 mt-2.5 sm:mt-3">
                          <button
                            onClick={() => fileInputRefs.current[doc.key]?.click()}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-colors"
                            data-testid={`upload-file-${doc.key}`}
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Choisir un fichier
                          </button>
                          <button
                            onClick={() => cameraInputRefs.current[doc.key]?.click()}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors"
                            data-testid={`upload-camera-${doc.key}`}
                          >
                            <Camera className="w-3.5 h-3.5" />
                            Prendre une photo
                          </button>
                        </div>
                      )}

                      {/* Replace button */}
                      {(isUploaded || wasAlreadySubmitted) && !isUploading && (
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => fileInputRefs.current[doc.key]?.click()}
                            className="text-xs text-slate-500 hover:text-emerald-600 active:text-emerald-700 underline"
                          >
                            Remplacer
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Hidden file inputs */}
                    <input
                      type="file"
                      ref={el => fileInputRefs.current[doc.key] = el}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadForDoc(doc.key, file);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                    <input
                      type="file"
                      ref={el => cameraInputRefs.current[doc.key] = el}
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadForDoc(doc.key, file);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Complementary documents */}
        <div className="bg-card rounded-xl sm:rounded-2xl shadow-md p-4 sm:p-5 mb-4">
          <button
            onClick={() => setShowComplementary(!showComplementary)}
            className="flex items-center justify-between w-full"
          >
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 flex items-center gap-2">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Documents complémentaires
            </h3>
            {showComplementary ? (
              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            )}
          </button>
          
          {showComplementary && (
            <div className="mt-3">
              <p className="text-[10px] sm:text-xs text-slate-500 mb-3">
                Vous pouvez ajouter des documents supplémentaires ici.
              </p>
              
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => complementaryRef.current?.click()}
                  disabled={uploadingComplementary}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 active:bg-slate-300 transition-colors disabled:opacity-50"
                >
                  {uploadingComplementary ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Ajouter un fichier
                </button>
                <button
                  onClick={() => complementaryCameraRef.current?.click()}
                  disabled={uploadingComplementary}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-lg hover:bg-blue-200 active:bg-blue-300 transition-colors disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  Prendre une photo
                </button>
              </div>

              <input
                type="file"
                ref={complementaryRef}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleComplementaryUpload(file);
                  e.target.value = '';
                }}
                className="hidden"
              />
              <input
                type="file"
                ref={complementaryCameraRef}
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleComplementaryUpload(file);
                  e.target.value = '';
                }}
                className="hidden"
              />

              {complementaryFiles.length > 0 && (
                <ul className="space-y-1.5">
                  {complementaryFiles.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5 p-2 bg-slate-50 rounded-lg">
                      {getFileIcon(f.filename)}
                      <span className="flex-1 text-xs sm:text-sm text-slate-700 truncate">{f.filename}</span>
                      <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Success message */}
        {allRequiredDone && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-5 text-center mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2.5">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-emerald-800 mb-1">Merci !</h3>
            <p className="text-emerald-700 text-xs sm:text-sm">
              Tous vos documents ont été envoyés. Votre conseiller RENOVA les examinera dans les meilleurs délais.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-5 text-center text-slate-500 text-[10px] sm:text-xs pb-4">
          <p>Des questions ? Contactez votre conseiller RENOVA Solution.</p>
          <p className="mt-1 text-slate-400">
            &copy; {new Date().getFullYear()} RENOVA Solution - Spécialiste de la rénovation énergétique
          </p>
        </div>
      </main>
    </div>
  );
}
